import React, { useState, useEffect, memo } from "react";
import { Card, CardContent, CardMedia, Typography, Box, Modal, IconButton, Tooltip } from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";

function ProductCard({ product }) {
	const [open, setOpen] = useState(false);
	const [imageLoadError, setImageLoadError] = useState(false);

	const rawImageUrl = product.image_url ?? product.URL;
	const hasValidImage = (() => {
		if (rawImageUrl == null) return false;
		const s = String(rawImageUrl).trim();
		if (s === "" || s === "-" || s === "N/A") return false;
		const lower = s.toLowerCase();
		if (lower === "null" || lower === "undefined") return false;
		return true;
	})();

	/** Tall packshots (common for footwear) + cover/top crop hides the product; use contain + bottom bias when covering */
	const footwearLike = (() => {
		const d = String(product.division || "").toUpperCase();
		const cat = String(product.category || "").toUpperCase();
		const sub = String(product.sub_category || "").toUpperCase();
		return (
			d.includes("FOOT") ||
			cat.includes("SNEAKER") ||
			cat.includes("SHOE") ||
			cat.includes("FOOTWEAR") ||
			cat.includes("SANDAL") ||
			cat.includes("BOOT") ||
			sub.includes("SNEAKER") ||
			sub.includes("SHOE") ||
			sub.includes("FOOTWEAR")
		);
	})();

	/** Full width of card; height scales by breakpoint (footwear = taller band for packshots) */
	const imageStageSx = {
		position: "relative",
		width: "100%",
		...(footwearLike
			? {
					height: {
						xs: "clamp(200px, 58vw, 270px)",
						sm: "clamp(215px, 40vw, 300px)",
						md: "clamp(225px, 22vw, 320px)",
					},
				}
			: {
					height: {
						xs: "clamp(160px, 50vw, 230px)",
						sm: "clamp(175px, 34vw, 260px)",
						md: "clamp(185px, 18vw, 280px)",
					},
				}),
	};

	useEffect(() => {
		setImageLoadError(false);
	}, [rawImageUrl, product.PKEY]);

	const showImage = hasValidImage && !imageLoadError;

	const handleOpen = () => {
		if (showImage) setOpen(true);
	};
	const handleClose = () => setOpen(false);

	// Format numbers with commas but no decimals
	const formatCurrency = (value) => {
		if (!value || value === "N/A") return "N/A";
		// Remove any existing commas and handle string "N/A"
		const cleanValue = value.toString().replace(/,/g, "");
		// Parse the number and round it
		const numValue = Math.round(parseFloat(cleanValue));
		// Check if it's a valid number
		if (isNaN(numValue)) return "N/A";
		// Format with commas but no decimals
		return numValue.toLocaleString("en-IN");
	};

	const formatNumber = (num) => {
		if (!num) return "0";
		return Math.round(Number(num)).toLocaleString("en-IN");
	};

	// Calculate average aging across all channels with non-zero values
	const calculateAverageAging = () => {
		const agingValues = [product.ebo_aging || 0, product.lfs_aging || 0, product.mbo_aging || 0, product.ecom_aging || 0].filter(
			(value) => value > 0
		); // Only include non-zero values

		if (agingValues.length === 0) return 0;
		return Math.round(agingValues.reduce((sum, value) => sum + value, 0) / agingValues.length);
	};

	// Calculate total WH SOH across all channels
	const calculateTotalWHSOH = () => {
		return product.total_wh_soh || 0;
	};

	// Calculate total EBO SOH across all channels
	const calculateTotalEBOSOH = () => {
		return product.total_ebo_soh || 0;
	};

	// Calculate average DOH across all channels with non-zero values
	const calculateAverageDOH = () => {
		const dohValues = [product.ebo_doh || 0, product.lfs_doh || 0, product.mbo_doh || 0, product.ecom_doh || 0].filter(
			(value) => value > 0
		); // Only include non-zero values

		if (dohValues.length === 0) return 0;
		return Math.round((dohValues.reduce((sum, value) => sum + value, 0) / dohValues.length) * 10) / 10;
	};

	// Create tooltip content for DOH details with black and white theme
	const getDOHTooltipContent = () => {
		const channels = [
			{ name: "EBO", value: product.ebo_doh || 0 },
			{ name: "LFS", value: product.lfs_doh || 0 },
			{ name: "MBO", value: product.mbo_doh || 0 },
			{ name: "ECOM", value: product.ecom_doh || 0 },
		].filter((channel) => channel.value > 0); // Only show channels with non-zero DOH

		if (channels.length === 0) return "No DOH data available";

		return (
			<Box
				sx={{
					backgroundColor: "#ffffff",
					color: "#000000",
					p: 1.5,
					borderRadius: 1,
					minWidth: 200,
					boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
					border: "1px solid #ffffff",
				}}>
				<Typography
					variant="body2"
					sx={{
						fontWeight: "bold",
						mb: 1.5,
						color: "#000000",
						fontSize: "0.8125rem",
						borderBottom: "1px solid #ffffff",
						pb: 0.5,
					}}>
					Channel-wise DOH:
				</Typography>
				{channels.map((channel, index) => (
					<Box
						key={index}
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							py: 0.3,
							borderBottom: index < channels.length - 1 ? "1px solid #cccccc" : "none",
						}}>
						<Typography
							variant="body2"
							sx={{
								fontSize: "0.75rem",
								color: "#666666",
								fontWeight: 500,
							}}>
							{channel.name}:
						</Typography>
						<Typography
							variant="body2"
							sx={{
								fontSize: "0.75rem",
								color: "#000000",
								fontWeight: "bold",
							}}>
							{channel.value} days
						</Typography>
					</Box>
				))}
			</Box>
		);
	};

	// Create tooltip content for T.SALE details with channel breakdown
	const getTSaleTooltipContent = () => {
		const channels = [
			{ name: "D2C", value: product.d2c_sold_qty || 0 },
			{ name: "Marketplace", value: product.marketplace_sold_qty || 0 },
			{ name: "LFS", value: product.lfs_sold_qty || 0 },
			{ name: "MBO", value: product.mbo_sold_qty || 0 },
			{ name: "EBO", value: product.ebo_sold_qty || 0 },
		].filter((channel) => channel.value > 0); // Only show channels with non-zero sales

		if (channels.length === 0) return "No sales data available";

		return (
			<Box
				sx={{
					backgroundColor: "#ffffff",
					color: "#000000",
					p: 1.5,
					borderRadius: 1,
					minWidth: 200,
					boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
					border: "1px solid #ffffff",
				}}>
				<Typography
					variant="body2"
					sx={{
						fontWeight: "bold",
						mb: 1.5,
						color: "#000000",
						fontSize: "0.8125rem",
						borderBottom: "1px solid #ffffff",
						pb: 0.5,
					}}>
					Channel Sales Breakdown:
				</Typography>
				{channels.map((channel, index) => (
					<Box
						key={index}
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							py: 0.3,
							borderBottom: index < channels.length - 1 ? "1px solid #cccccc" : "none",
						}}>
						<Typography
							variant="body2"
							sx={{
								fontSize: "0.75rem",
								color: "#666666",
								fontWeight: 500,
							}}>
							{channel.name}:
						</Typography>
						<Typography
							variant="body2"
							sx={{
								fontSize: "0.75rem",
								color: "#000000",
								fontWeight: "bold",
							}}>
							{channel.value}
						</Typography>
					</Box>
				))}
			</Box>
		);
	};

	// Create tooltip content for SOH details with black and white theme (matching Aging style)
	const getSOHTooltipContent = () => {
		const whSoh = calculateTotalWHSOH();
		const eboSoh = calculateTotalEBOSOH();

		if (whSoh === 0 && eboSoh === 0) return "No SOH data available";

		return (
			<Box
				sx={{
					backgroundColor: "#ffffff",
					color: "#000000",
					p: 1.5,
					borderRadius: 1,
					minWidth: 200,
					boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
					border: "1px solid #ffffff",
				}}>
				<Typography
					variant="body2"
					sx={{
						fontWeight: "bold",
						mb: 1.5,
						color: "#000000",
						fontSize: "0.8125rem",
						borderBottom: "1px solid #cccccc",
						pb: 0.5,
					}}>
					Stock On Hand Details:
				</Typography>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						py: 0.3,
						borderBottom: "1px solid #cccccc",
					}}>
					<Typography
						variant="body2"
						sx={{
							fontSize: "0.75rem",
							color: "#666666",
							fontWeight: 500,
						}}>
						WH SOH:
					</Typography>
					<Typography
						variant="body2"
						sx={{
							fontSize: "0.75rem",
							color: "#000000",
							fontWeight: "bold",
						}}>
						{formatNumber(whSoh)}
					</Typography>
				</Box>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						py: 0.3,
					}}>
					<Typography
						variant="body2"
						sx={{
							fontSize: "0.75rem",
							color: "#666666",
							fontWeight: 500,
						}}>
						EBO SOH:
					</Typography>
					<Typography
						variant="body2"
						sx={{
							fontSize: "0.75rem",
							color: "#000000",
							fontWeight: "bold",
						}}>
						{formatNumber(eboSoh)}
					</Typography>
				</Box>
			</Box>
		);
	};

	// Create tooltip content for channel-level aging details with black and white theme
	const getAgingTooltipContent = () => {
		const channels = [
			{ name: "EBO", value: product.ebo_aging || 0 },
			{ name: "LFS", value: product.lfs_aging || 0 },
			{ name: "MBO", value: product.mbo_aging || 0 },
			{ name: "ECOM", value: product.ecom_aging || 0 },
		].filter((channel) => channel.value > 0); // Only show channels with non-zero aging

		if (channels.length === 0) return "No aging data available";

		return (
			<Box
				sx={{
					backgroundColor: "#ffffff",
					color: "#000000",
					p: 1.5,
					borderRadius: 1,
					minWidth: 200,
					boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
					border: "1px solid #ffffff",
				}}>
				<Typography
					variant="body2"
					sx={{
						fontWeight: "bold",
						mb: 1.5,
						color: "#000000",
						fontSize: "0.8125rem",
						borderBottom: "1px solid #ffffff",
						pb: 0.5,
					}}>
					Channel-wise Aging:
				</Typography>
				{channels.map((channel, index) => (
					<Box
						key={index}
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							py: 0.3,
							borderBottom: index < channels.length - 1 ? "1px solid #cccccc" : "none",
						}}>
						<Typography
							variant="body2"
							sx={{
								fontSize: "0.75rem",
								color: "#666666",
								fontWeight: 500,
							}}>
							{channel.name}:
						</Typography>
						<Typography
							variant="body2"
							sx={{
								fontSize: "0.75rem",
								color: "#000000",
								fontWeight: "bold",
							}}>
							{channel.value} days
						</Typography>
					</Box>
				))}
			</Box>
		);
	};

	// Create tooltip content for ROS details with channel-level breakdown
	const getROSTooltipContent = () => {
		// Calculate ROS for each channel based on aging and sales data
		const channels = [
			{
				name: "EBO",
				aging: product.ebo_aging || 0,
				sales: product.ebo_sold_qty || 0,
				ros: product.ebo_aging > 0 ? Math.round((product.ebo_sold_qty || 0) / product.ebo_aging) : 0,
			},
			{
				name: "LFS",
				aging: product.lfs_aging || 0,
				sales: product.lfs_sold_qty || 0,
				ros: product.lfs_aging > 0 ? Math.round((product.lfs_sold_qty || 0) / product.lfs_aging) : 0,
			},
			{
				name: "MBO",
				aging: product.mbo_aging || 0,
				sales: product.mbo_sold_qty || 0,
				ros: product.mbo_aging > 0 ? Math.round((product.mbo_sold_qty || 0) / product.mbo_aging) : 0,
			},
			{
				name: "ECOM",
				aging: product.ecom_aging || 0,
				sales: product.ecom_sold_qty || 0,
				ros: product.ecom_aging > 0 ? Math.round((product.ecom_sold_qty || 0) / product.ecom_aging) : 0,
			},
		].filter((channel) => channel.aging > 0); // Only show channels with aging data

		if (channels.length === 0) return "No ROS data available";

		return (
			<div
				style={{
					backgroundColor: "#ffffff",
					color: "#000000",
					padding: "12px",
					borderRadius: "4px",
					boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
					border: "1px solid #ffffff",
					minWidth: "250px",
					fontFamily: "Inter, sans-serif",
				}}>
				<div
					style={{
						fontWeight: "bold",
						marginBottom: "12px",
						color: "#000000",
						fontSize: "11px",
						borderBottom: "1px solid #e0e0e0",
						paddingBottom: "4px",
					}}>
					Channel-wise ROS (Rate of Sale):
				</div>
				{channels.map((channel, index) => (
					<div
						key={index}
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							padding: "4px 0",
							borderBottom: index < channels.length - 1 ? "1px solid #e0e0e0" : "none",
						}}>
						<span
							style={{
								fontSize: "11px",
								color: "#666666",
								fontWeight: 500,
							}}>
							{channel.name}:
						</span>
						<span
							style={{
								fontSize: "11px",
								color: "#000000",
								fontWeight: "bold",
							}}>
							{channel.ros} ({channel.sales} units / {channel.aging} days)
						</span>
					</div>
				))}
			</div>
		);
	};

	return (
		<>
			<Card
				sx={{
					height: "100%",
					display: "flex",
					flexDirection: "column",
					borderRadius: 0,
					boxShadow: "none",
					border: "none",
					"& .MuiCardMedia-root": {
						borderRadius: 0,
					},
				}}>
				<Box
					sx={{
						...imageStageSx,
						flexShrink: 0,
						...(showImage && footwearLike
							? {
									backgroundColor: "#ebebeb",
									overflow: "hidden",
								}
							: {}),
						...(showImage
							? {
									"&:hover .zoom-icon": {
										opacity: 1,
									},
								}
							: {}),
					}}
				>
					{showImage ? (
						<>
							<CardMedia
								component="img"
								image={rawImageUrl}
								alt={product.PKEY}
								onClick={handleOpen}
								onError={() => setImageLoadError(true)}
								sx={{
									display: "block",
									width: "100%",
									height: "100%",
									maxWidth: "100%",
									// Footwear: tall canvas + white top — cover + bottom anchor crops empty space, fills tile (no tiny shoe + overlap)
									...(footwearLike
										? {
												objectFit: "cover",
												objectPosition: "center bottom",
											}
										: {
												objectFit: "cover",
												objectPosition: "center center",
											}),
									borderRadius: 0,
									cursor: "pointer",
								}}
							/>
							<IconButton
								className="zoom-icon"
								onClick={handleOpen}
								sx={{
									position: "absolute",
									left: "50%",
									...(footwearLike
										? {
												bottom: 6,
												top: "auto",
												transform: "translateX(-50%)",
											}
										: {
												top: "50%",
												transform: "translate(-50%, -50%)",
											}),
									backgroundColor: "rgba(0, 0, 0, 0.5)",
									color: "white",
									opacity: 0,
									transition: "opacity 0.3s ease",
									"&:hover": {
										backgroundColor: "rgba(0, 0, 0, 0.7)",
									},
								}}>
								<ZoomInIcon />
							</IconButton>
						</>
					) : (
						<Box
							sx={{
								height: "100%",
								width: "100%",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								backgroundColor: "#f5f5f5",
								borderBottom: "1px solid #e0e0e0",
								color: "#757575",
								px: 2,
							}}>
							<Typography variant="body2" sx={{ fontWeight: 500, textAlign: "center", fontSize: "0.8125rem" }}>
								{hasValidImage && imageLoadError ? "Image could not be loaded" : "No Image Found"}
							</Typography>
						</Box>
					)}
				</Box>
				<CardContent
					sx={{
						p: 1,
						"&:last-child": { pb: 1 },
						borderRadius: 0,
					}}>
					<Typography
						variant="subtitle2"
						gutterBottom
						sx={{ fontWeight: "bold", mb: 0.5, textAlign: "center", fontSize: "0.75rem" }}>
						OPTION: {product.PKEY}
					</Typography>

					{/* 8x8 Grid Layout */}
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "repeat(8, 1fr)",
							gap: 0.5,
							fontSize: "0.6rem",
							textAlign: "center",
						}}>
						{/* Row 1 */}
						<Box sx={{ gridColumn: "1 / 3", textAlign: "left" }}>
							<Typography variant="body2" sx={{ fontSize: "0.6rem", fontWeight: "bold" }}>
								Season:
							</Typography>
							<Typography variant="body2" sx={{ fontSize: "0.6rem" }}>
								{product.season || "N/A"}
							</Typography>
						</Box>
						<Box sx={{ gridColumn: "3 / 5", textAlign: "left" }}>
							<Typography variant="body2" sx={{ fontSize: "0.6rem", fontWeight: "bold" }}>
								Category:
							</Typography>
							<Typography variant="body2" sx={{ fontSize: "0.6rem" }}>
								{product.category || "N/A"}
							</Typography>
						</Box>
						<Box sx={{ gridColumn: "5 / 7", textAlign: "left" }}>
							<Typography variant="body2" sx={{ fontSize: "0.6rem", fontWeight: "bold" }}>
								MRP:
							</Typography>
							<Typography variant="body2" sx={{ fontSize: "0.6rem" }}>
								{formatCurrency(product.troco_ls?.mrp)}
							</Typography>
						</Box>
						<Box sx={{ gridColumn: "7 / 9", textAlign: "left" }}>
							<Typography variant="body2" sx={{ fontSize: "0.6rem", fontWeight: "bold" }}>
								COGS:
							</Typography>
							<Typography variant="body2" sx={{ fontSize: "0.6rem" }}>
								{formatCurrency(product.COGS)}
							</Typography>
						</Box>

						{/* Row 2 */}
						<Box sx={{ gridColumn: "1 / 3", textAlign: "left" }}>
							<Typography variant="body2" sx={{ fontSize: "0.6rem", fontWeight: "bold" }}>
								T.INW:
							</Typography>
							<Typography variant="body2" sx={{ fontSize: "0.6rem" }}>
								{formatNumber(product.troco_ls?.t_inw)}
							</Typography>
						</Box>
						<Tooltip
							title={getTSaleTooltipContent()}
							arrow
							placement="top"
							slotProps={{
								tooltip: {
									sx: {
										backgroundColor: "transparent",
										border: "none",
										boxShadow: "none",
										"& .MuiTooltip-arrow": {
											color: "#ffffff",
										},
									},
								},
							}}>
							<Box sx={{ gridColumn: "3 / 5", textAlign: "left", cursor: "help" }}>
								<Typography variant="body2" sx={{ fontSize: "0.6rem", fontWeight: "bold" }}>
									T.SALE:
								</Typography>
								<Typography variant="body2" sx={{ fontSize: "0.6rem" }}>
									{formatNumber(product.troco_ls?.t_sale)}
								</Typography>
							</Box>
						</Tooltip>
						<Box sx={{ gridColumn: "5 / 7", textAlign: "left" }}>
							<Typography variant="body2" sx={{ fontSize: "0.6rem", fontWeight: "bold" }}>
								ST:
							</Typography>
							<Typography variant="body2" sx={{ fontSize: "0.6rem" }}>
								{((product.troco_ls?.t_sale / product.troco_ls?.t_buy) * 100 || 0).toFixed(2)}%
							</Typography>
						</Box>
						<Box sx={{ gridColumn: "7 / 9", textAlign: "left" }}>
							<Typography variant="body2" sx={{ fontSize: "0.6rem", fontWeight: "bold" }}>
								GP%:
							</Typography>
							<Typography variant="body2" sx={{ fontSize: "0.6rem" }}>
								{product.lcogs?.gp_percentage || "0.00%"}
							</Typography>
						</Box>

						{/* Row 3 */}
						<Box sx={{ gridColumn: "1 / 3", textAlign: "left" }}>
							<Typography variant="body2" sx={{ fontSize: "0.6rem", fontWeight: "bold" }}>
								GP Value:
							</Typography>
							<Typography variant="body2" sx={{ fontSize: "0.6rem" }}>
								{product.lcogs?.gp_value || 0}
							</Typography>
						</Box>
						<Box sx={{ gridColumn: "3 / 5", textAlign: "left" }}>
							<Typography variant="body2" sx={{ fontSize: "0.6rem", fontWeight: "bold" }}>
								Online:
							</Typography>
							<Typography variant="body2" sx={{ fontSize: "0.6rem" }}>
								{product.lcogs?.oln_cont || "0%"} ({product.online_sold_qty || 0})
							</Typography>
						</Box>
						<Box sx={{ gridColumn: "5 / 7", textAlign: "left" }}>
							<Typography variant="body2" sx={{ fontSize: "0.6rem", fontWeight: "bold" }}>
								Offline:
							</Typography>
							<Typography variant="body2" sx={{ fontSize: "0.6rem" }}>
								{product.lcogs?.offln_cont || "0%"} ({product.offline_sold_qty || 0})
							</Typography>
						</Box>
						<Tooltip
							title={getSOHTooltipContent()}
							arrow
							placement="top"
							slotProps={{
								tooltip: {
									sx: {
										backgroundColor: "transparent",
										border: "none",
										boxShadow: "none",
										"& .MuiTooltip-arrow": {
											color: "#ffffff",
										},
									},
								},
							}}>
							<Box sx={{ gridColumn: "7 / 9", textAlign: "left", cursor: "help" }}>
								<Typography variant="body2" sx={{ fontSize: "0.6rem", fontWeight: "bold" }}>
									SOH:
								</Typography>
								<Typography variant="body2" sx={{ fontSize: "0.6rem" }}>
									{calculateTotalWHSOH() + calculateTotalEBOSOH()}
								</Typography>
							</Box>
						</Tooltip>

						{/* Row 4 */}
						<Tooltip
							title={getAgingTooltipContent()}
							arrow
							placement="top"
							slotProps={{
								tooltip: {
									sx: {
										backgroundColor: "transparent",
										border: "none",
										boxShadow: "none",
										"& .MuiTooltip-arrow": {
											color: "#ffffff",
										},
									},
								},
							}}>
							<Box sx={{ gridColumn: "1 / 3", textAlign: "left", cursor: "help" }}>
								<Typography variant="body2" sx={{ fontSize: "0.6rem", fontWeight: "bold" }}>
									Avg Aging:
								</Typography>
								<Typography variant="body2" sx={{ fontSize: "0.6rem" }}>
									{calculateAverageAging()} days
								</Typography>
							</Box>
						</Tooltip>
						<Tooltip
							title={getDOHTooltipContent()}
							arrow
							placement="top"
							slotProps={{
								tooltip: {
									sx: {
										backgroundColor: "transparent",
										border: "none",
										boxShadow: "none",
										"& .MuiTooltip-arrow": {
											color: "#ffffff",
										},
									},
								},
							}}>
							<Box sx={{ gridColumn: "3 / 5", textAlign: "left", cursor: "help" }}>
								<Typography variant="body2" sx={{ fontSize: "0.6rem", fontWeight: "bold" }}>
									Avg DOH:
								</Typography>
								<Typography variant="body2" sx={{ fontSize: "0.6rem" }}>
									{calculateAverageDOH()} days
								</Typography>
							</Box>
						</Tooltip>
						<Tooltip
							title={getROSTooltipContent()}
							arrow
							placement="top"
							slotProps={{
								tooltip: {
									sx: {
										backgroundColor: "transparent",
										border: "none",
										boxShadow: "none",
										"& .MuiTooltip-arrow": {
											color: "#ffffff",
										},
									},
								},
							}}>
							<Box sx={{ gridColumn: "5 / 7", textAlign: "left", cursor: "help" }}>
								<Typography variant="body2" sx={{ fontSize: "0.6rem", fontWeight: "bold" }}>
									ROS:
								</Typography>
								<Typography variant="body2" sx={{ fontSize: "0.6rem" }}>
									{Math.round(product.rate_of_sale || 0)}
								</Typography>
							</Box>
						</Tooltip>
						<Box sx={{ gridColumn: "7 / 9", textAlign: "left" }}>
							<Typography variant="body2" sx={{ fontSize: "0.6rem", fontWeight: "bold" }}>
								BLEND:
							</Typography>
							<Typography variant="body2" sx={{ fontSize: "0.6rem" }}>
								{product.blend || "N/A"}
							</Typography>
						</Box>
					</Box>
				</CardContent>
			</Card>

			{/* Zoom Modal */}
			<Modal
				open={open}
				onClose={handleClose}
				aria-labelledby="image-modal"
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					"& .MuiBackdrop-root": {
						backgroundColor: "rgba(0, 0, 0, 0.9)",
					},
				}}>
				<Box
					sx={{
						maxWidth: "90vw",
						maxHeight: "90vh",
						outline: "none",
						bgcolor: "background.paper",
						p: 1,
					}}
					onClick={handleClose}>
					{showImage ? (
						<img
							src={rawImageUrl}
							alt={product.PKEY}
							style={{
								width: "100%",
								height: "100%",
								maxWidth: "100%",
								maxHeight: "90vh",
								objectFit: "contain",
								objectPosition: footwearLike ? "center bottom" : "center center",
							}}
						/>
					) : (
						<Typography sx={{ p: 4, color: "text.secondary" }}>No Image Found</Typography>
					)}
				</Box>
			</Modal>
		</>
	);
}

export default memo(ProductCard);
